import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";

import PagedTable from "../shared/Tables/PagedTable";
import { IDepollutionQueueRow } from "../../models/Depollution";
import { IVehicle } from "../../models/Vehicle";
import TableVehicleDetails from "../vehicles/VehiclesTable/TableVehicleDetails";
import useDepollution from "../../data/useDepollution";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import useNotification from "../notifications/useNotifications";

const colHelper = createColumnHelper<IDepollutionQueueRow>();

export default function DepollutionTable() {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { vrmLookup } = useVehiclesMutations();
  const { createDepollution } = useDepollution();
  const depollutionLabel = "Depollution";

  const handleLookupAndGo = async (query: string) => {
    const vrm = query?.replace(/\s/g, "").toUpperCase();
    if (!vrm) {
      addNotification({
        variant: "warn",
        primaryText: "Enter a VRM first",
      });
      return;
    }

    try {
      const vehicleResponse = await vrmLookup.mutateAsync(vrm);
      const vehicle = vehicleResponse?.data;

      if (!vehicle?.id) {
        addNotification({
          variant: "error",
          primaryText: "Vehicle lookup returned no result",
        });
        return;
      }

      if (vehicle.depollutionId) {
        navigate(`/depollution/${vehicle.depollutionId}`);
        return;
      }

      const depollutionResponse = await createDepollution.mutateAsync({ vehicleId: vehicle.id });
      navigate(`/depollution/${depollutionResponse.data.id}`);
    } catch (error) {
      addNotification({
        variant: "error",
        primaryText: `Unable to lookup VRM and start ${depollutionLabel.toLowerCase()}`,
      });
    }
  };

  const columns = useMemo(
    () => [
      colHelper.accessor("vrm", {
        id: "Vehicle.Vrm",
        header: "VRM",
      }),
      colHelper.accessor("stockNumber", {
        id: "Vehicle.StockNumber",
        header: "Stock #",
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "vehicleDetails",
        header: "Vehicle",
        enableSorting: false,
        cell: (info) => {
          const row = info.row.original;
          const vehicle = {
            id: row.vehicleId,
            vrm: row.vrm ?? "",
            stockNumber: row.stockNumber ?? "",
            combinedMake: row.combinedMake ?? "",
            combinedModel: row.combinedModel ?? "",
            modelRangeDescription: row.modelRangeDescription ?? "",
            engineModelCode: row.engineModelCode ?? "",
            combinedVIN: "",
            transmission: "",
            fuelType: "",
            vehicleImageUrl: row.vehicleImageUrl ?? "",
          } as IVehicle;

          return <TableVehicleDetails vehicle={vehicle} />;
        },
      }),
      colHelper.accessor("engineModelCode", {
        id: "Vehicle.EngineModelCode",
        header: "Engine Code",
        cell: (info) => info.getValue() ?? "-",
      }),
      colHelper.display({
        id: "status",
        header: depollutionLabel,
        enableSorting: false,
        cell: (info) => (
          <span>{info.row.original.completedSteps}/{info.row.original.totalSteps}</span>
        ),
      }),
    ],
    [depollutionLabel]
  );

  return (
    <PagedTable<IDepollutionQueueRow>
      key="depollution-queue"
      url="/depollution/filtered"
      columns={columns}
      queryLabel="Search VRM, stock, make, model"
      queryButton={{
        label: "Go",
        onClick: handleLookupAndGo,
        isLoading: vrmLookup.isLoading || createDepollution.isLoading,
      }}
      showTotal={true}
      onRowClick={(row) => navigate(`/depollution/${row.id}`)}
    />
  );
}
